/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, Type, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons/faEnvelopeOpenText';
import { faFileCsv } from '@fortawesome/free-solid-svg-icons/faFileCsv';
import { faKey } from '@fortawesome/free-solid-svg-icons/faKey';
import { BehaviorSubject, forkJoin, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, skip, switchMap, take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { GroupSharedService } from '../../../services/shared/group-shared/group-shared.service';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { faCheckSquare } from '@fortawesome/free-regular-svg-icons/faCheckSquare';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { OrganisationCollaboratorInterface } from '../../../models/interface/organisation/organisation-collaborator.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { Action, Store } from '@ngrx/store';
import { HelperService } from '../../../services/helper/helper.service';
import { MediaGalleryComponent } from '../../media/media-gallery/media-gallery.component';
import { GetOrganisationCollaboratorsSuccess, OrganisationActionTypes } from '../../../store/organisation/organisation.action';
import { HttpResponse } from '@angular/common/http';
import { Actions, ofType } from '@ngrx/effects';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { StudyActionTypes } from '../../../store/study/study.action';
import { InterventionActionTypes } from '../../../store/intervention/intervention.action';
import { getCollabInterventionsByStudyId } from '../../../store/intervention/intervention.selector';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { StudyMediaInterface } from '../../../models/interface/study/study-media.interface';
import { ReminderStore } from 'src/app/store/reminder/component-store/reminder.store';
import { UserStore } from 'src/app/store/user/component-store/user.store';
import { UserSharedService } from 'src/app/services/shared/user-shared/user-shared.service';

type Tabs =
  | 'interventions'
  | 'members'
  | 'collaborators'
  | 'instances'
  | 'tasks'
  | 'activities'
  | 'diaryInstances'
  | 'configuration'
  | 'review'
  | 'buddies';

/**
 * Component:
 * Group details page displaying a navigation bar for its child component;
 * Can be found: {uri}/groups/{{group_id}}
 */

@Component({
  selector: 'app-group-detail',
  templateUrl: './group-detail.component.html',
  styleUrls: ['./group-detail.component.scss'],
  providers: [ReminderStore, StudyStore, UserStore],
})
export class GroupDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('matTabGroup')
  matTabGroup: QueryList<MatTabGroup>;

  @ViewChild('mediaGalleryContainer', { read: ViewContainerRef })
  mediaGalleryContainer: ViewContainerRef;

  public components: MediaGalleryComponent[];

  // Icons
  faEnvelopeOpenText = faEnvelopeOpenText;
  faKey = faKey;
  faCheckSquare = faCheckSquare;
  faFileCsv = faFileCsv;

  public isLoading$: Observable<boolean>;

  // Data provided by StudyService
  public group: StudyInterface;
  public collaborators: UserInterface[];

  public profile: ProfileInterface;

  public isECoach$: Observable<boolean>;
  public isManager$: Observable<boolean>;
  public isOwner$: Observable<boolean>;
  public isPublisher$: Observable<boolean>;
  public supportsBuddy$: Observable<boolean>;

  public profile$: Observable<ProfileInterface> = null;

  public studyDetail$: Observable<StudyInterface>;

  public supportsBuddySubject = new BehaviorSubject<boolean>(false);
  public isOrganisationCollaboratorSubject = new BehaviorSubject<boolean>(false);

  private translationSubject = new BehaviorSubject<any>({});

  private studyMedia$: Observable<StudyMediaInterface[]>;

  private getCSVReminderLogsOfStudyResponse$: Observable<any>;

  private allInterventionsOfStudy$: Observable<InterventionInterface[]>;

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;
  private allCollaborators$: Observable<UserInterface[]>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private isECoachStudySubject = new BehaviorSubject<boolean>(false);
  private isManagerSubject = new BehaviorSubject<boolean>(false);
  private isOwnerSubject = new BehaviorSubject<boolean>(false);
  private isPublisherSubject = new BehaviorSubject<boolean>(false);
  private isOrganisationManagerSubject = new BehaviorSubject<boolean>(false);

  private subscriptions: Subscription[] = [];

  constructor(
    private actRoute: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private studyStore: StudyStore,
    private reminderStore: ReminderStore,
    private userStore: UserStore,
    private actions$: Actions,
    private store: Store<{
      myProfile: ProfileInterface;
      getCollabInterventionsByStudyId: Array<InterventionInterface>;
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
    }>,
    private sharedService: GroupSharedService,
    private userSharedService: UserSharedService,
  ) {
    this.profile$ = store.select('myProfile');
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.isECoach$ = this.isECoachStudySubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.isOwner$ = this.isOwnerSubject.asObservable();
    this.isPublisher$ = this.isPublisherSubject.asObservable();
    this.supportsBuddy$ = this.supportsBuddySubject.asObservable();
    this.studyDetail$ = this.studyStore.studyDetail$ as Observable<StudyInterface>;
    this.studyMedia$ = this.studyStore.studyMedia$;
    this.getCSVReminderLogsOfStudyResponse$ = this.reminderStore.getCSVReminderLogsOfStudyResponse$;
    this.allCollaborators$ = this.userStore.allCollaborators$;
    this.subscriptions.push(
      this.translateService
        .getTranslation(localStorage.getItem('language') !== null ? localStorage.getItem('language') : 'en')
        .subscribe(
          (translations) => this.translationSubject.next(translations),
        ),
    );
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  public get translation() {
    return this.translateService;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.profile$
        .pipe(
          filter((user) => user !== null),
          take(1),
          switchMap((result: ProfileInterface) => {
            this.profile = result;
            return this.actRoute.params;
          }),
          switchMap((param) => {
            this.studyStore.getStudyDetail({ studyId: param.id });
            return this.studyDetail$.pipe(skip(1), take(1));
          }),
          switchMap((result: StudyInterface) => {
            this.group = result;
            if (this.group.attributes.organisation_id !== null) {
              const organisationId = parseInt(this.group.attributes.organisation_id.toString(), 10);
              this.store.dispatch({
                type: OrganisationActionTypes.getOrganisationCollaboratorsType,
                payload: { organisationId },
              });
              this.userStore.getAllCollaboratorsOfUser({ roles: 'ecoach' });
              const allCollaborators = this.allCollaborators$.pipe(skip(1), take(1), catchError(() => of([] as UserInterface[])));
              const organisationCollaboratorResult = this.actions$.pipe(
                ofType(
                  OrganisationActionTypes.getOrganisationCollaboratorsSuccessType,
                  OrganisationActionTypes.getOrganisationCollaboratorsErrorType,
                ),
                take(1),
                mergeMap((action: Action) => {
                  if (action.type === OrganisationActionTypes.getOrganisationCollaboratorsSuccessType) {
                    const resp: HttpResponse<any> = (action as GetOrganisationCollaboratorsSuccess).response;
                    const collaborators: OrganisationCollaboratorInterface[] = resp.body?.data;
                    const profileId = this.profile.id.toString();
                    const myself = collaborators?.find((user) => user.id.toString() === profileId);
                    if (myself) {
                      const myRoleSlug = this.helperService.getHighestRoleOfOrganisationCollaborator(myself);
                      if (myRoleSlug !== '') {
                        this.isOrganisationCollaboratorSubject.next(true);
                        if (myRoleSlug === 'organisation.manager' || myRoleSlug === 'organisation.owner') {
                          return of(true);
                        }
                      }
                    }
                  }
                  return of(false);
                }),
                catchError(() => of(false)),
              );
              return forkJoin({ allCollaborators, organisationCollaboratorResult }).pipe(
                mergeMap((results) => {
                  this.userSharedService.pushCollaborators(results.allCollaborators);
                  return of(results.organisationCollaboratorResult);
                }),
              );
            } else {
              this.userStore.getAllCollaboratorsOfUser({ roles: 'ecoach' });
              return this.allCollaborators$.pipe(
                skip(1),
                take(1),
                mergeMap((result) => {
                  this.userSharedService.pushCollaborators(result);
                  return of(false);
                }),
                catchError(() => {
                  this.userSharedService.pushCollaborators([]);
                  return of(false);
                }),
              );
            }
          }),
          mergeMap((result: boolean) => {
            this.isOrganisationManagerSubject.next(result);
            this.sharedService.pushIsOrganisationManager(result);
            this.sharedService.pushGroup(this.group);

            this.store.dispatch({
              type: InterventionActionTypes.getInterventionsOfCollabStudyType,
              payload: { typeOfParentStudy: 'study' },
            });

            this.allInterventionsOfStudy$ = this.store.select(getCollabInterventionsByStudyId(this.group.id));
            return this.allInterventionsOfStudy$.pipe(skip(1), take(1));
          }),
          mergeMap((interventions: InterventionInterface[]) => {
            this.supportsBuddySubject.next(
              interventions.some((intervention) => intervention.attributes.buddy_support === 1),
            );
            return this.getInterventionContent();
          }),
          catchError((error) => throwError(() => error)),
        )
        .subscribe({
          next: () => {
            this.isLoadingSubject.next(false);
          },
          error: () => {
            this.router.navigateByUrl('/groups');
          },
        }),
    );

    this.subscriptions.push(
      this.translateService.onLangChange
        .pipe(
          switchMap((event: LangChangeEvent) => {
            this.isLoadingSubject.next(true);
            this.translationSubject.next(event.translations);
            return this.getInterventionContent();
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      this.sharedService.sourceGroup$.subscribe((group: StudyInterface) => {
        this.group = group;
      }),
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.matTabGroup.changes.subscribe((_comps: QueryList<MatTabGroup>) => {
        this.helper.setMatTabActive(
          this.matTabGroup,
          this.router.url,
          this.translationSubject,
          this.translateService,
          'group-detail',
        );
      })
    );
  }

  // Display and navigate to child component relative to this path
  public showChildComponent(child: Tabs | ''): void {
    if (child !== '') {
      this.router.navigate([child], { relativeTo: this.actRoute });
    }
  }

  public initializeMediaGalleryComponent(componentClass: Type<any>, studyId: number): void {
    this.components = [];
    this.mediaGalleryContainer.clear();
    const component = this.mediaGalleryContainer.createComponent<MediaGalleryComponent>(componentClass).instance;

    this.studyStore.getStudyMedia({ studyId });
    this.studyMedia$.pipe(skip(1), take(1)).subscribe((result) => {
      component._studyMedia = result;
      component._studyId = studyId;
    });
    this.components = [...this.components, component];
  }

  // Listens to tab change
  public onChangeTab(event: MatTabChangeEvent): void {
    const matTab = event.tab;
    const tab = this.evaluateTab(matTab.textLabel, this.translationSubject.value);
    this.showChildComponent(tab);
  }

  public openDialogStudyReport(): void {
    this.helperDialog
      .openDialogStudyReport(this.group)
      .afterClosed()
      .subscribe(() => {});
  }

  public openDialogCodeBook(): void {
    this.helperDialog
      .openDialogCodeBook(this.group)
      .afterClosed()
      .subscribe(() => {});
  }

  public getDetailedOrganisation(organisationId: number): void {
    this.router.navigateByUrl(`/organisations/${organisationId}`);
  }

  public generateCSVReminderLogsReport(): void {
    this.reminderStore.getReminderLogsOfStudyCSV({ studyId: this.group.id });
    this.getCSVReminderLogsOfStudyResponse$.subscribe();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private evaluateTab(label: string, translate: any): Tabs | '' {
    if (translate) {
      if (translate['group-detail']['interventions'] === label) {
        return 'interventions';
      }
      if (translate['group-detail']['members'] === label) {
        return 'members';
      }
      if (translate['group-detail']['collaborators'] === label) {
        return 'collaborators';
      }
      if (translate['group-detail']['instances'] === label) {
        return 'instances';
      }
      if (translate['group-detail']['tasks'] === label) {
        return 'tasks';
      }
      if (translate['group-detail']['activities'] === label) {
        return 'activities';
      }
      if (translate['group-detail']['diaries'] === label) {
        return 'diaryInstances';
      }
      if (translate['group-detail']['configurations'] === label) {
        return 'configuration';
      }
      if (translate['group-detail']['review'] === label) {
        return 'review';
      }
      if (translate['group-detail']['buddies'] === label) {
        return 'buddies';
      }
    }
    return '';
  }

  // Get and display detailed study details
  private getInterventionContent(): Observable<any> {
    return this.actRoute.params.pipe(
      map(v => v.id),
      take(1),
      switchMap((id) => {
        this.studyStore.getStudyDetail({ studyId: id });
        const studyDetails = this.studyDetail$.pipe(skip(1), take(1));
        this.store.dispatch({
          type: StudyActionTypes.getCollaboratorsType,
          payload: { studyId: id, include: 'roles' },
        });
        this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(id));
        const studyCollaborators = this.collaboratorsOfStudy$.pipe(
          filter(collaboratorsOfStudy => !!collaboratorsOfStudy),
          take(1),
        );
        return forkJoin({ studyDetails, studyCollaborators });
      }),
      mergeMap((results) => {
        this.group = results.studyDetails;
        this.collaborators = results.studyCollaborators.collaborators;

        // Determine my highest role
        const profileId = this.profile.id.toString();
        const myself: UserInterface = this.collaborators.find(
          (user) => user.id.toString() === profileId,
        );
        if (myself !== undefined) {
          const myRoleSlug = this.helper.getHighestRoleOfCollaborator(myself);

          const isOwner = myRoleSlug === 'study.owner';
          const isManager = isOwner || myRoleSlug === 'study.ecoachmanager';
          const isECoach = isManager || myRoleSlug === 'study.ecoach';

          this.isECoachStudySubject.next(isECoach);
          this.sharedService.pushIsECoach(isECoach);

          this.isManagerSubject.next(isManager);
          this.sharedService.pushIsManager(isManager);

          this.isPublisherSubject.next(this.helper.hasRoles(myself.relationships?.roles.data, /study\.publisher$/));
          this.sharedService.pushIsPublisher(this.isPublisherSubject.value);

          if (isOwner) {
            this.isOwnerSubject.next(true);
          }

          this.sharedService.pushIsCollaborating(true);
        } else {
          this.sharedService.pushIsCollaborating(false);
        }

        this.isLoadingSubject.next(false);
        return of(null);
      }),
      catchError((error) => throwError(() => error)),
    );
  }
}
